import {
    CreatePointRequest,
    CreateTagRequest,
    CreateTopicRequest,
    CreateTopicResponse,
    DiscoveryLevel,
    GetPointResponse,
    LoginOrigin,
    ObjectId,
    TopicCategory,
    TopicCreationSource,
    TopicSearchResult
} from '../types';
import { ZERO_OBJECT_ID } from '../helpers/constants';
import { LitElement, css, html } from 'lit';
import { TEMPLATE_CATEGORIES, addTopic, getTopic, getTopicsforBackpack } from '../helpers/api/topics-api';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { Checkbox } from '@material/mwc-checkbox';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import { addPoints } from '../helpers/api/points-api';
import { getCategoryIcon } from '../helpers/markers';
import { getMainPage } from '../helpers/page-name';
import { getSmallSocialImageForTopic } from '../helpers/files';
import { logWithAmplitude } from '../helpers/api/api';
import topicData from '../helpers/state/topic-data';

@customElement('add-point-to-map-component')
export class AddPointToMapComponent extends LitElement {
    static amplitude_callsite: string = 'Add Point To Map';

    @query('#pinned_from')
    pinnedFromElement?: Checkbox;

    @property({ type: String })
    emailAddress?: string;

    @property({ type: String })
    one_time_code?: string;

    @property({ type: Boolean })
    show_one_time_code: boolean = false;



    @query('#search')
    searchElement?: TextField;

    @property()
    points?: Array<GetPointResponse | undefined>;

    @state()
    currentTopic: TopicSearchResult | undefined;

    @state()
    currentCategory: TopicCategory | undefined;

    @state()
    categories: TopicCategory[] | undefined;

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    topicsToRender: TopicSearchResult[] = [];

    @state()
    currentMapLink: string | undefined;

    @state()
    includePinnedFrom: boolean = true;

    private readonly bindAccountInfo = new StateController(this, accountInfo);

    protected async firstUpdated() {
        this.topics = await getTopicsforBackpack(AddPointToMapComponent.amplitude_callsite);
        this.categories = topicData.categories;
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topics')) {
            this.topicsToRender = this.topics;
        }
    }

    private readonly searchChanged = debounce(this.searchChangedInternal, 500);
    private searchChangedInternal() {
        const searchText = this.searchElement?.value?.trim().toLocaleLowerCase() ?? '';

        this.topicsToRender = this.topics.filter((topic) => {
            return topic.name.toLocaleLowerCase().includes(searchText);
        });

        logWithAmplitude(AddPointToMapComponent.amplitude_callsite, 'Topic Search', {
            search: searchText
        });
    }

    handleChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    handleChangeOneTimeCode(e: any) {
        this.one_time_code = e.detail.value;
    }

    private async handleSubmit() {
        const email = this.emailAddress ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(AddPointToMapComponent.amplitude_callsite, email);
            this.show_one_time_code = true;
            this.requestUpdate();
        }, 'An error has occurred.  Context: could not request an authentication token.');
    }

    private async handleSubmitOneTimeCode(organization_id?: string) {
        const email = this.emailAddress ?? '';
        const one_time_code = this.one_time_code ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                AddPointToMapComponent.amplitude_callsite,
                email,
                one_time_code,
                undefined,
                LoginOrigin.SigninModule
            );
            await accountInfo.instantiateAccountData();
            const separator = !window.location.href.includes('?') ? '?' : '&';
            window.location.href = window.location.href + separator + 'addToModal=true';
        }, 'An error has occurred.  Context: could not upgrade the authentication token.');
    }

    private async handleNewEmail() {
        await accountInfo.logout();
        this.show_one_time_code = false;
        this.requestUpdate();
    }

    private onSelectTopic(topic: TopicSearchResult) {
        this.currentTopic = topic;
    }

    private onGoBack() {
        this.currentMapLink = undefined;
        this.currentTopic = undefined;
        this.currentCategory = undefined;
    }

    private onSelectCategory(category: TopicCategory) {
        this.currentCategory = category;
    }

    private async createNewMap() {
        const tag_info: CreateTagRequest = {
            user_tags: []
        };

        const email = accountInfo.email;

        const createTopicRequest: CreateTopicRequest = {
            name: 'My Proxi Map',
            email,
            organization_id: undefined,
            use_organization_template: false,
            creator: undefined,
            categories: this.categories ?? (TEMPLATE_CATEGORIES as TopicCategory[]),
            has_dates: false,
            should_filter_past_events: false,
            has_likes: false,
            discovery_level: DiscoveryLevel.Private,
            disable_google_images: false,
            hide_header: false,
            show_advanced_filter_row: false,
            custom_font: undefined,
            requires_approval: false,
            send_crowdsource_email: true,
            default_map_center: undefined,
            default_map_zoom: undefined,
            default_map_rotation: undefined,
            default_map_tilt: undefined,
            header_logo: undefined,
            header_url: undefined,
            header_color: undefined,
            header_text_color: undefined,
            image_display: undefined,
            default_list_view: undefined,
            cluster_accent_color: undefined,
            translation_languages: undefined,
            custom_map_style: undefined,
            custom_map_style_name: undefined,
            custom_social_media_image: undefined,
            marker_cluster_minimum: undefined,
            marker_cluster_threshold: undefined,
            icon_size: undefined,
            search_pill_settings: undefined,
            share_settings: undefined,
            featured_color: undefined,
            featured_name: undefined,
            custom_map_geojson: undefined,
            custom_map_geojson_style: undefined,
            custom_shapes: [],
            custom_overlays: [],
            show_easy_date_filters: undefined,
            tag_info,
            creation_source: TopicCreationSource.INTERNAL_FROM_POINT,
            pop_ups: undefined,
            comment_settings: undefined,
            add_point_customization: undefined,
            point_email_capture_customization: undefined,
        };

        let createTopicResponse: CreateTopicResponse;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            createTopicResponse = await addTopic(
                AddPointToMapComponent.amplitude_callsite,
                createTopicRequest
            );

            const newTopic = await getTopic(
                AddPointToMapComponent.amplitude_callsite,
                createTopicResponse?._id.$oid,
                createTopicResponse.write_key,
                false
            );

            if (this.points === undefined) {
                return;
            }

            const requestsArray: CreatePointRequest[] = [];
            for (const point of this.points) {
                const pointCategory = (
                    this.categories ?? (TEMPLATE_CATEGORIES as TopicCategory[])
                ).find((c) => c._id?.$oid === point?.topic_category_id.$oid);

                const createPointRequest: CreatePointRequest = {
                    _id: undefined,
                    email: undefinedIfEmpty(email),
                    topic_id: createTopicResponse._id,
                    topic_category_id:
                        newTopic.categories.find(
                            (newCategory) => newCategory.name === pointCategory?.name
                        )?._id ?? newTopic.categories[0]?._id ?? { $oid: ZERO_OBJECT_ID },
                    location: point?.location ?? {
                        search: '',
                        place_id: '',
                        latLng: {
                            lat: 0,
                            lng: 0
                        }
                    },
                    name: point?.name,
                    description: point?.description,
                    url: point?.url,
                    special_offer: undefined,
                    embedded_video: point?.embedded_video,
                    custom_icon_url: point?.custom_icon_url,
                    active: true,
                    featured: false,
                    start: point?.start,
                    end: point?.end,
                    source: {
                        source_type: 'ProxiMap',
                        source_url: `${window.location.origin}/#discover-details?topic_id=${topicData.topicId ?? ''}&point_id=${point?._id?.$oid ?? ''}`,
                        source_name: createTopicRequest?.name
                    },
                    tag_info: point?.tag_info ?? { user_tags: [] },
                    number: undefined,
                    url_images: undefined,
                    is_gate_hidden: false,
                    custom_information: point?.custom_information,
                    custom_shape: point?.custom_shape,
                };
                requestsArray.push(createPointRequest);
            }

            await addPoints(
                AddPointToMapComponent.amplitude_callsite,
                createTopicResponse?._id.$oid,
                requestsArray,
                createTopicResponse.write_key
            );

            // Open Link
            if (createTopicResponse.should_use_keyless_links) {
                window.open(createTopicResponse.cc_account_admin_link, '_blank');
            } else {
                window.open(createTopicResponse.cc_write_link, '_blank');
            }
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
    }

    private async updatePinnedFrom() {
        if (this.pinnedFromElement?.checked === true) {
            this.includePinnedFrom = true;
        } else {
            this.includePinnedFrom = false;
        }
    }

    private async addPointToExistingTopic() {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const email = accountInfo.email;

            if (this.points === undefined) {
                return;
            }

            const requestsArray: CreatePointRequest[] = [];
            for (const point of this.points) {
                const pinningSourceInfo = {
                        source_type: 'ProxiMap',
                        source_url: `${window.location.origin}/#discover-details?topic_id=${
                            topicData.topicId ?? ''
                        }&point_id=${point?._id.$oid ?? ''}`,
                        source_name: topicData.topic?.name ?? ''
                    };
                const pinnedFrom = this.includePinnedFrom ? pinningSourceInfo : undefined;

                const createPointRequest: CreatePointRequest = {
                    _id: undefined,
                    email: undefinedIfEmpty(email),
                    topic_id: this.currentTopic?._id as ObjectId,
                    topic_category_id: this.currentCategory?._id as ObjectId,
                    location: point?.location ?? {
                        search: '',
                        place_id: '',
                        latLng: {
                            lat: 0,
                            lng: 0
                        }
                    },
                    name: point?.name,
                    description: point?.description,
                    url: point?.url,
                    special_offer: undefined,
                    embedded_video: point?.embedded_video,
                    custom_icon_url: point?.custom_icon_url,
                    custom_shape: point?.custom_shape,
                    active: true,
                    featured: false,
                    start: point?.start,
                    end: point?.end,
                    source: pinnedFrom,
                    tag_info: point?.tag_info ?? {
                        user_tags: []
                    },
                    number: undefined,
                    url_images: undefined,
                    is_gate_hidden: false,
                    custom_information: point?.custom_information
                };
                requestsArray.push(createPointRequest);
            }

            await addPoints(
                AddPointToMapComponent.amplitude_callsite,
                this.currentTopic?._id.$oid ?? '',
                requestsArray,
                ''
            );

            const createTopicResponse = await getTopic(
                AddPointToMapComponent.amplitude_callsite,
                this.currentTopic?._id?.$oid ?? '',
                '',
                false
            );

            this.currentMapLink = createTopicResponse.cc_write_link;
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
    }

    private viewMap() {
        if (isDefined(this.currentMapLink)) {
            window.open(this.currentMapLink, '_blank');
        }
    }

    private closeModal() {
        this.onGoBack();
        this.dispatchEvent(new CustomEvent('close-modal', {}));
    }

    renderFirstScreen() {
        return html`
            <div class="authForm">
                <form-input
                    id="name"
                    type="email"
                    label="Email address"
                    placeholder="Enter your email"
                    value="${this.emailAddress ?? ''}"
                    @change="${this.handleChangeEmailAddress}"
                >
                </form-input>
                <div class="flexContainer">
                    <span>You will receive a one time code that you will enter here.</span>
                </div>
                <form-button
                    type="submit"
                    id="signinBtn"
                    @click="${this.handleSubmit}"
                    ?fullWidth=${true}
                >
                    Send My One Time Code
                </form-button>
            </div>
        `;
    }

    renderSecondScreen() {
        return html`
            <div class="authForm">
                <h4 class="modal-title">Email Sent</h4>
                <p class="code-description">
                    Check your email <b>${this.emailAddress}</b> and enter the 6 digit code here:
                </p>
                <form-input
                    id="name"
                    type="string"
                    label="Verification Code"
                    placeholder="000000"
                    value="${this.one_time_code ?? ''}"
                    @change="${this.handleChangeOneTimeCode}"
                >
                </form-input>
                <form-button
                    type="submit"
                    id="signinBtn"
                    @click="${this.handleSubmitOneTimeCode}"
                    ?fullWidth=${true}
                >
                    Log In
                </form-button>
                <div class="tryAgainLink">
                    Didn't receive the email? Check your spam folder or
                    <span class="click-resend" @click=${this.handleSubmit}>click to resend</span>.
                </div>
                <div class="tryAgainLink" @click=${this.handleNewEmail}>
                    <mwc-icon>arrow_back</mwc-icon>
                    <span>Try Another Email</span>
                </div>
            </div>
        `;
    }

    render() {
        let modalTitle = 'Add a point to Your Map';
        let endTitle = 'The point successfully added';
        let buttonTitle = 'Add the point to another map';
        if (this.points && this.points?.length > 1) {
            modalTitle = `Add ${this.points?.length} points to Your Map`;
            endTitle = `${this.points?.length} points successfully added`;
            buttonTitle = 'Add the points to another map';
        }
        if (isDefined(this.currentTopic)) {
            modalTitle = 'Select a Category';
        }

        if (!accountInfo?.email) {
            return html`
                <div class="modalContainer">
                    <h3 class="modalTitle">${modalTitle}</h3>
                    ${this.show_one_time_code
                        ? this.renderSecondScreen()
                        : this.renderFirstScreen()}
                    <error-toast id="errorToast"></error-toast>
                </div>
            `;
        }

        if (isDefined(this.currentMapLink)) {
            return html` <div class="modalContainer">
                    <div class="allDone">
                        <mwc-icon>thumb_up</mwc-icon>
                        <h3>${endTitle}</h3>
                    </div>
                    <div class="actionButtonGroup">
                        <form-button size="md" fullWidth variant="outline" @click="${this.onGoBack}"
                            >${buttonTitle}</form-button
                        >
                        <form-button size="md" fullWidth variant="outline" @click="${this.viewMap}"
                            >View Map</form-button
                        >
                        <form-button size="md" fullWidth @click="${this.closeModal}"
                            >Close</form-button
                        >
                    </div>
                </div>
                <error-toast id="errorToast"></error-toast>`;
        }

        return html`
            <div class="modalContainer">
                <h3 class="modalTitle">${modalTitle}</h3>
                <div class="scrollableContent">
                    ${isDefined(this.currentTopic)
                        ? html`
                              <div class="headSection">
                                  <mwc-icon-button
                                      @click="${this.onGoBack}"
                                      raised
                                      icon="chevron_left"
                                  ></mwc-icon-button>
                                  <span>${this.currentTopic?.name}</span>
                              </div>
                              <div class="categoriesWrapper">
                                  ${this.currentTopic.categories.length > 0
                                      ? this.currentTopic.categories.map((c) => {
                                            return html`
                                                <div
                                                    class="category-chip"
                                                    @click="${() => this.onSelectCategory(c)}"
                                                    active
                                                >
                                                    ${this.currentCategory?._id?.$oid ===
                                                    c?._id?.$oid
                                                        ? html`
                                                              <mwc-icon
                                                                  class="metadata-icon"
                                                                  outlined
                                                                  >check</mwc-icon
                                                              >
                                                          `
                                                        : html` <img src="${getCategoryIcon(c._id)}" /> `}
                                                    <span>${c.name}</span>
                                                </div>
                                            `;
                                        })
                                      : ''}
                                 </div>
                            ${topicData.isWriter ? html`
                                <p> Administrator Option: </p>
                                <div class="pinned_from_option">
                                    <mwc-checkbox
                                        id="pinned_from"
                                        ?checked=${true}
                                        @change=${this.updatePinnedFrom}
                                    >
                                    </mwc-checkbox>
                                    <span>Include a link to this source map on the points</span>
                                </div>
                                ` : ''}
                        ` : html`
                              <div class="topicsWrapper">
                                  <div class="form-input-button-wrapper">
                                      <mwc-textfield
                                          id="search"
                                          class="input"
                                          label="Search"
                                          @input="${this.searchChanged}"
                                      >
                                      </mwc-textfield>
                                  </div>
                                  <div>
                                      ${this.topicsToRender.map((topic) => {
                                          const backgroundImage = isDefined(topic)
                                              ? getSmallSocialImageForTopic(topic)
                                              : '';
                                          return html` <div
                                              class="topic-item"
                                              @click="${() => this.onSelectTopic(topic)}"
                                          >
                                              <div class="topic-img">
                                                  <img
                                                      src="${backgroundImage}"
                                                      class="backpack-card__image"
                                                      alt="card-img"
                                                  />
                                              </div>
                                              <div class="topic-content">
                                                  <div class="topic-metadata">
                                                      <span class="metadata-item">
                                                          <mwc-icon class="metadata-icon" outlined
                                                              >place</mwc-icon
                                                          >
                                                          ${topic?.statistics
                                                              .approximate_point_count}
                                                          points
                                                      </span>
                                                      <span class="metadata-item">
                                                          <mwc-icon class="metadata-icon" outlined
                                                              >visibility</mwc-icon
                                                          >
                                                          ${topic?.statistics
                                                              .approximate_topic_views}
                                                          views
                                                      </span>
                                                  </div>
                                                  <h4 class="topic-name">${topic.name}</h4>
                                              </div>
                                          </div>`;
                                      })}
                                  </div>
                              </div>
                          `}
                </div>
                <div class="actionButtonGroup">
                    ${isDefined(this.currentTopic)
                        ? html`
                              <form-button
                                  size="md"
                                  class="actionButton"
                                  fullWidth
                                  .disabled="${!isDefined(this.currentCategory)}"
                                  @click="${this.addPointToExistingTopic}"
                                  >Add Now</form-button
                              >
                          `
                        : html`
                              <form-button
                                  size="md"
                                  class="actionButton"
                                  fullWidth
                                  @click="${this.createNewMap}"
                                  >Or create a new map</form-button
                              >
                          `}
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .modalContainer {
                display: flex;
                flex-direction: column;
                width: 550px;
                max-width: 100%;
            }
            .authForm {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .scrollableContent {
                flex: 1;
                overflow: auto;
            }
            /* width */
            .scrollableContent::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .scrollableContent::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .scrollableContent::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }
            .topic-item {
                display: flex;
                margin: 1rem 0;
                max-height: 100px;
                border-radius: 0.75rem;
                overflow: hidden;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }
            .topic-item:hover {
                background-color: var(--theme-lightest-color);
            }
            .backpack-card__image, .topic-img {
                max-height: 100px;
            }
            .metadata-item {
                display: flex;
                align-items: center;
                color: var(--theme-input-color);
                font-size: 14px;
                gap: 0.25rem;
            }
            .metadata-icon {
                color: var(--theme-lighter-text-color);
                --mdc-icon-size: 12px;
            }
            .topic-metadata {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }
            .topic-content {
                flex: 1;
                padding: 0.75rem 1.5rem;
            }
            .topic-name {
                margin: 0.5rem 0;
            }
            .modalTitle {
                text-align: center;
                margin: 1.5rem 0;
                font-size: 24px;
            }
            .form-input-button-wrapper {
                margin-bottom: 1.5rem;
            }
            #search {
                width: 100%;
            }
            .headSection {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                font-size: 16px;
                font-weight: 600;
            }
            .categoriesWrapper {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                margin: 1rem 0;
            }
            .pinned_from_option{
                display: flex;
                align-items: center;
                flex-direction: row;
                gap: 0.5rem;
                margin: 1rem 0;

            }
            .category-chip {
                border-radius: 3rem;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
                padding: 0.25rem 1rem 0.25rem 0.5rem;
                display: flex;
                align-items: center;
                gap: 0.5rem;
                border: 1px solid var(--theme-border-color);
                color: var(--theme-primary-blue);
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
            }
            .metadata-icon {
                --mdc-icon-size: 18px;
                color: var(--theme-primary-blue);
                font-weight: 600;
            }
            .category-chip img {
                width: 1.5rem;
                height: 1.5rem;
            }
            .actionButtonGroup {
                margin-top: 1rem;
                display: flex;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            .actionButtonGroup form-button {
                flex: 1;
            }
            .allDone {
                flex: 1;
                display: flex;
                flex-direction: column;
                margin-top: 2rem;
                justify-content: center;
                align-items: center;
                text-align: center;
            }
        `;
    }
}
